/**
 * Persistence port for `WorkspaceLayoutEnvelope` (ADR-UI-003 §4). A sibling
 * of `../layout-store.ts`'s `LayoutStore`, not a modification of it — that
 * port stays concretely typed to the retired `WorkspaceLayout` shape so the
 * old `WorkspaceStore`/`operations-route.tsx` keep working unchanged until
 * Round B's cutover. Same localStorage-backed default / in-memory test double
 * shape as that file; the port makes a future M25 preferences-API backing a
 * non-breaking swap.
 */
import type { WorkspaceLayoutEnvelope } from "../types";

export interface WorkspaceEnvelopeStore {
  /** Returns the raw persisted value (unknown, to be migrated) or `null`. */
  load(key: string): Promise<unknown>;
  save(key: string, envelope: WorkspaceLayoutEnvelope): Promise<void>;
  clear(key: string): Promise<void>;
}

const PREFIX = "thw.workspace.flexlayout.";

/** Default browser implementation. Fails soft when storage is unavailable. */
export const localStorageWorkspaceEnvelopeStore: WorkspaceEnvelopeStore = {
  load(key) {
    try {
      const raw = window.localStorage.getItem(`${PREFIX}${key}`);
      if (raw === null) return Promise.resolve(null);
      return Promise.resolve(JSON.parse(raw) as unknown);
    } catch {
      return Promise.resolve(null);
    }
  },
  save(key, envelope) {
    try {
      window.localStorage.setItem(`${PREFIX}${key}`, JSON.stringify(envelope));
    } catch {
      // A layout that cannot be persisted must not break the live session.
    }
    return Promise.resolve();
  },
  clear(key) {
    try {
      window.localStorage.removeItem(`${PREFIX}${key}`);
    } catch {
      // Ignore: the in-memory reset already happened.
    }
    return Promise.resolve();
  },
};

/** In-memory implementation for tests, demos and SSR. */
export class InMemoryWorkspaceEnvelopeStore implements WorkspaceEnvelopeStore {
  private readonly records = new Map<string, string>();

  load(key: string): Promise<unknown> {
    const raw = this.records.get(key);
    if (raw === undefined) return Promise.resolve(null);
    return Promise.resolve(JSON.parse(raw) as unknown);
  }

  save(key: string, envelope: WorkspaceLayoutEnvelope): Promise<void> {
    this.records.set(key, JSON.stringify(envelope));
    return Promise.resolve();
  }

  clear(key: string): Promise<void> {
    this.records.delete(key);
    return Promise.resolve();
  }

  /** Test helper: has anything been persisted under `key`? */
  has(key: string): boolean {
    return this.records.has(key);
  }
}
